import React, { useEffect, useRef } from 'react';
import { Menu, Shield, Settings, LogOut, Sun, Moon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from '../ui/sheet';
import { ThemeToggle } from '../ui/ThemeToggle';
import { NAV_ITEMS } from './DesktopNav';
import { announceToScreenReader } from '../../lib/accessibility';

// Rainbow Logo Component
const RainbowLogo = ({ isDark }) => (
  <span className={`text-left flex ${isDark ? 'text-white' : 'text-gray-900'}`}>
    <span className="text-red-500">I</span>
    <span className="text-orange-500 ml-0.5">G</span>
    <span className="text-yellow-500">o</span>
    <span className="text-green-500">t</span>
    <span className="text-blue-500 ml-1">S</span>
    <span className="text-indigo-500">o</span>
    <span className="text-purple-500">u</span>
    <span className="text-pink-500">l</span>
  </span>
);

export const MobileMenu = ({ 
  isOpen, 
  setIsOpen, 
  isDark, 
  user, 
  onLogout 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  // Announce menu state changes
  useEffect(() => {
    if (isOpen) {
      announceToScreenReader('Navigation menu opened');
    }
  }, [isOpen]);

  const handleNavigate = (path, label) => {
    navigate(path);
    setIsOpen(false);
    announceToScreenReader(`Navigated to ${label}`);
  };

  const handleLogout = () => {
    onLogout();
    setIsOpen(false);
    announceToScreenReader('Signed out successfully');
  };

  // Handle keyboard navigation within menu
  const handleKeyDown = (e, index, items) => {
    const focusableItems = navRef.current?.querySelectorAll('button[role="menuitem"]');
    if (!focusableItems) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (index + 1) % focusableItems.length;
        focusableItems[nextIndex]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = index === 0 ? focusableItems.length - 1 : index - 1;
        focusableItems[prevIndex]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        focusableItems[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        focusableItems[focusableItems.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button 
          className={`md:hidden flex items-center justify-center w-11 h-11 rounded-xl active:scale-95 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2 ${
            isDark 
              ? 'text-gray-300 hover:text-white hover:bg-white/10 active:bg-white/20'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200'
          }`}
          data-testid="mobile-menu-btn"
          aria-label="Open navigation menu"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          <Menu className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
        </button>
      </SheetTrigger>
      <SheetContent 
        side="left" 
        className={`w-72 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
        aria-label="Navigation menu"
      >
        <SheetHeader>
          <SheetTitle>
            <RainbowLogo isDark={isDark} />
          </SheetTitle>
          <SheetDescription className="sr-only">
            Main navigation menu. Use arrow keys to navigate between items.
          </SheetDescription>
        </SheetHeader>
        <nav 
          ref={navRef}
          className="mt-6 space-y-1" 
          role="menu" 
          aria-label="Main navigation"
        >
          {NAV_ITEMS.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path, item.label)}
                onKeyDown={(e) => handleKeyDown(e, index, NAV_ITEMS)}
                role="menuitem"
                tabIndex={0}
                aria-current={isActive ? 'page' : undefined}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left
                  transition-all active:scale-98
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-inset
                  ${isActive 
                    ? 'bg-yellow-400/10 text-yellow-400 font-medium' 
                    : isDark
                      ? 'text-gray-300 hover:bg-white/5 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                {isActive && (
                  <span className="w-1 h-6 bg-yellow-400 rounded-r-full absolute left-0" aria-hidden="true" />
                )}
                <span>{item.label}</span>
                {isActive && <span className="sr-only">(current page)</span>}
              </button>
            );
          })}
          
          <div className={`border-t my-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`} role="separator" />
          
          {/* Theme Toggle in Mobile Menu */}
          <div className="px-4 py-2">
            <div 
              className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}
              role="group"
              aria-label="Theme settings"
            >
              <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {isDark ? <Moon className="w-4 h-4" aria-hidden="true" /> : <Sun className="w-4 h-4" aria-hidden="true" />}
                {isDark ? 'Dark Mode' : 'Light Mode'}
              </span>
              <ThemeToggle variant="ghost" size="sm" />
            </div>
          </div>
          
          <button
            onClick={() => handleNavigate('/account-settings', 'Account Settings')}
            onKeyDown={(e) => handleKeyDown(e, NAV_ITEMS.length, NAV_ITEMS)}
            role="menuitem"
            tabIndex={0}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-inset ${
              isDark 
                ? 'text-gray-300 hover:bg-white/5 hover:text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <Settings className="w-5 h-5" aria-hidden="true" />
            <span>Account Settings</span>
          </button>
          
          {user?.role === 'admin' && (
            <button
              onClick={() => handleNavigate('/admin', 'Admin Portal')}
              onKeyDown={(e) => handleKeyDown(e, NAV_ITEMS.length + 1, NAV_ITEMS)}
              role="menuitem"
              tabIndex={0}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-yellow-400 hover:bg-yellow-400/10 transition-all font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-inset"
              data-testid="mobile-admin-link"
            >
              <Shield className="w-5 h-5" aria-hidden="true" />
              <span>Admin Portal</span>
            </button>
          )}
          
          <button
            onClick={handleLogout}
            role="menuitem"
            tabIndex={0}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-inset"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" aria-hidden="true" />
            <span>Sign Out</span>
          </button>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
